import { useMemo, useState, type JSX } from 'react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { FormField } from '@/components/form';
import {
  PROPERTY_MEDIA_ALLOWED_TYPES,
  PROPERTY_MEDIA_MAX_BYTES,
  PROPERTY_MEDIA_MAX_FILES,
} from '@/lib/constants/propertyMedia';
import {
  isAllowedPropertyMediaType,
  isWithinPropertyMediaSizeLimit,
  parsePropertyMediaUrl,
  removePropertyMediaFiles,
  uploadPropertyMediaFile,
} from '@/lib/storage/propertyMedia';

interface UnitGalleryImageFieldProps {
  propertyId: string;
  galleryImageUrls: string[];
  disabled: boolean;
  error?: string;
  onGalleryImageUrlsChange: (updater: (current: string[]) => string[]) => void;
  onUploadingChange: (uploading: boolean) => void;
}

export function UnitGalleryImageField({
  propertyId,
  galleryImageUrls,
  disabled,
  error,
  onGalleryImageUrlsChange,
  onUploadingChange,
}: UnitGalleryImageFieldProps): JSX.Element {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const maxImageSizeGb = useMemo(
    () => Math.round(PROPERTY_MEDIA_MAX_BYTES / (1024 * 1024 * 1024)),
    [],
  );
  const acceptTypes = useMemo(() => PROPERTY_MEDIA_ALLOWED_TYPES.join(','), []);

  const mediaList = useMemo(
    () => galleryImageUrls.map((url) => url.trim()).filter((url) => url.length > 0),
    [galleryImageUrls],
  );
  const isGalleryFull = mediaList.length >= PROPERTY_MEDIA_MAX_FILES;
  const canInteract = !disabled && !isUploading && !isGalleryFull;

  const setUploading = (next: boolean) => {
    setIsUploading(next);
    onUploadingChange(next);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const selectedFiles = Array.from(event.target.files ?? []);
    if (selectedFiles.length === 0) {
      return;
    }

    setUploadError(null);

    const remainingSlots = PROPERTY_MEDIA_MAX_FILES - mediaList.length;
    if (remainingSlots <= 0) {
      setUploadError(`You can upload up to ${PROPERTY_MEDIA_MAX_FILES.toString()} images.`);
      event.target.value = '';
      return;
    }

    const files = selectedFiles.slice(0, remainingSlots);
    if (selectedFiles.length > remainingSlots) {
      toast.info(
        `Uploading ${remainingSlots.toString()} of ${selectedFiles.length.toString()} selected images.`,
      );
    }

    const invalidType = files.find((file) => !isAllowedPropertyMediaType(file));
    if (invalidType) {
      setUploadError(`Unsupported file type: ${invalidType.type || invalidType.name}.`);
      event.target.value = '';
      return;
    }

    const tooLarge = files.find((file) => !isWithinPropertyMediaSizeLimit(file));
    if (tooLarge) {
      setUploadError(`Each image must be under ${maxImageSizeGb.toString()}GB.`);
      event.target.value = '';
      return;
    }

    setUploading(true);
    let uploadedCount = 0;

    try {
      for (const file of files) {
        const result = await uploadPropertyMediaFile({ propertyId, file });
        if (typeof result.url === 'string' && result.url.length > 0) {
          uploadedCount += 1;
          onGalleryImageUrlsChange((current) => [...current, result.url]);
        }
      }

      if (uploadedCount === 1) {
        toast.success('Uploaded photo.');
      } else if (uploadedCount > 1) {
        toast.success(`Uploaded ${uploadedCount.toString()} photos.`);
      }
    } catch (uploadFailure) {
      const message =
        uploadFailure instanceof Error ? uploadFailure.message : 'Upload failed. Please try again.';
      setUploadError(message);
      toast.error(message);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveMedia = async (url: string): Promise<void> => {
    setUploadError(null);
    setUploading(true);
    let removed = false;
    try {
      const parsed = parsePropertyMediaUrl(url);
      if (parsed) {
        await removePropertyMediaFiles([`${parsed.bucket}/${parsed.objectPath}`]);
      }
      removed = true;
    } catch (removeFailure) {
      const message =
        removeFailure instanceof Error
          ? removeFailure.message
          : 'Unable to remove image from storage.';
      setUploadError(message);
      toast.error(message);
    } finally {
      if (removed) {
        onGalleryImageUrlsChange((current) => current.filter((existing) => existing !== url));
        toast.success('Removed photo.');
      }
      setUploading(false);
    }
  };

  return (
    <FormField
      id="unit-gallery-images"
      label="Gallery images"
      description={`Optional. Shown in the unit photo gallery. Upload up to ${PROPERTY_MEDIA_MAX_FILES.toString()} images. Max ${maxImageSizeGb.toString()}GB each.`}
      error={uploadError ?? error}
    >
      <div className="flex flex-col gap-4">
        <label
          className={clsx(
            'inline-flex w-fit items-center justify-center rounded-full border border-surface-subtle px-4 py-2 text-sm font-semibold text-brand-primary transition focus-within:outline-none focus-within:ring-2 focus-within:ring-brand-primary/40 hover:bg-brand-primary/5',
            (disabled || isUploading) && 'opacity-60',
          )}
        >
          <input
            type="file"
            accept={acceptTypes}
            className="sr-only"
            multiple
            disabled={!canInteract}
            onChange={handleFileChange}
          />
          {isUploading ? 'Uploading…' : isGalleryFull ? 'Gallery full' : 'Upload photos'}
        </label>

        {mediaList.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {mediaList.map((url) => (
              <div
                key={url}
                className="flex flex-col overflow-hidden rounded-2xl border border-surface-subtle bg-white"
              >
                <img src={url} alt="Unit gallery" className="h-40 w-full object-cover" />
                <button
                  type="button"
                  className="border-t border-surface-subtle px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-60"
                  disabled={disabled || isUploading}
                  onClick={() => {
                    void handleRemoveMedia(url);
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-text-primary/70">
            No unit photos yet. Upload images to populate the unit gallery.
          </p>
        )}
      </div>
    </FormField>
  );
}
